// ── V170: 투자비 · 감가상각 · 투자 회수 · 누적 순이익률 ─────────────────────────────
// 사장님: "투자비 입력하면 감가상각 적용 기간을 선택할 수 있게 하고, 비용으로 처리하도록 해 줘. 회계상 처리 방식을 알려 주면 좋겠다.
//          투자비 회수 예상 시점을 알 수 있으면 좋고. 누적 순이익률도 알아야 할 것 같아. 진단을 할 때 이런 지점들도"
//
// 회계 처리 (관리 손익 기준)
//   · 인테리어·설비·집기처럼 여러 해 쓰는 것은 산 달에 한 번에 비용으로 잡지 않고 '유형자산'으로 두고,
//     쓰는 기간(내용연수)에 나눠 매달 '감가상각비'로 비용 처리한다 — 정액법(금액 ÷ 개월 수), 잔존가치 0, 산 달부터.
//   · 권리금은 '무형자산' — 같은 방식으로 나눠 상각.
//   · 보증금은 돌려받는 돈이라 비용이 아니다(자산) — 상각하지 않고, 투자 회수 대상에서도 뺀다.
//   · 감가상각비는 현금이 나가지 않는 비용 → 투자 회수는 '순이익 + 감가상각비'(영업 현금)로 계산한다.
// 저장: profitLoss._investments = { items: [{ id, type, name, amount, start: 'YYYY-MM', months }], openMonth }
//   (profitLoss의 다른 키는 'YYYY-MM' 월 기록. 월 목록을 셀 때는 /^\d{4}-\d{2}$/만)

export const ASSET_TYPES = [
  { id: 'interior',    ko: '인테리어·시설공사', zh: '装修·设施工程',  months: 60, kind: 'tangible' },
  { id: 'equipment',   ko: '주방 설비·기계',    zh: '厨房设备·机器',  months: 60, kind: 'tangible' },
  { id: 'furniture',   ko: '가구·집기·비품',    zh: '家具·器具·用品', months: 36, kind: 'tangible' },
  { id: 'electronics', ko: '전자기기·POS',      zh: '电子设备·收银机', months: 36, kind: 'tangible' },
  { id: 'vehicle',     ko: '차량',              zh: '车辆',           months: 48, kind: 'tangible' },
  { id: 'premium',     ko: '권리금',            zh: '转让费',         months: 60, kind: 'intangible' },
  { id: 'deposit',     ko: '보증금',            zh: '押金',           months: 0,  kind: 'deposit' },
  { id: 'other',       ko: '기타 투자',         zh: '其他投资',       months: 60, kind: 'tangible' },
];
export const ASSET_BY_ID = Object.fromEntries(ASSET_TYPES.map(a => [a.id, a]));
export const LIFE_OPTIONS = [12, 24, 36, 48, 60, 84, 120];   // 1 · 2 · 3 · 4 · 5 · 7 · 10년

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const isMonth = (m) => /^\d{4}-\d{2}$/.test(String(m || ''));
export const monthIndex = (m) => { const [y, mo] = String(m).split('-').map(Number); return y * 12 + (mo - 1); };
export const monthDiff = (from, to) => monthIndex(to) - monthIndex(from);
export function addMonths(m, n) {
  const i = monthIndex(m) + n;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
}
export const isDeposit = (it) => (ASSET_BY_ID[it && it.type] || {}).kind === 'deposit';

/** 저장된 투자비 (없으면 빈 것) */
export function investmentsOf(profitLoss) {
  const v = profitLoss && profitLoss._investments;
  return { items: Array.isArray(v && v.items) ? v.items.filter(Boolean) : [], openMonth: isMonth(v && v.openMonth) ? v.openMonth : '' };
}

/** 한 항목의 그 달 감가상각비 — 정액법, 산 달부터, 마지막 달에 끝자리 맞춤 */
export function depOfItem(it, month) {
  const amount = Number(it && it.amount) || 0;
  const months = Math.round(Number(it && it.months) || 0);
  if (!(amount > 0) || !(months > 0) || isDeposit(it) || !isMonth(it.start) || !isMonth(month)) return 0;
  const i = monthDiff(it.start, month);
  if (i < 0 || i >= months) return 0;
  const base = Math.floor((amount / months) * 100) / 100;
  return i === months - 1 ? r2(amount - base * (months - 1)) : base;
}
export const depForMonth = (items = [], month) => r2((items || []).reduce((a, it) => a + depOfItem(it, month), 0));

/** 그 달까지 상각 누계 · 남은 장부가액 */
export function bookValue(it, month) {
  const amount = Number(it && it.amount) || 0;
  if (isDeposit(it)) return { acc: 0, book: amount, endMonth: null };
  const months = Math.round(Number(it && it.months) || 0);
  let acc = 0;
  if (isMonth(it.start) && isMonth(month) && months > 0) {
    const upto = Math.min(months - 1, monthDiff(it.start, month));
    for (let i = 0; i <= upto; i++) acc += depOfItem(it, addMonths(it.start, i));
  }
  acc = r2(acc);
  return { acc, book: r2(amount - acc), endMonth: months > 0 && isMonth(it.start) ? addMonths(it.start, months - 1) : null };
}

export function investTotals(items = []) {
  let total = 0; let deposit = 0;
  for (const it of items || []) {
    const a = Number(it && it.amount) || 0;
    total += a;
    if (isDeposit(it)) deposit += a;
  }
  return { total: r2(total), deposit: r2(deposit), recoverable: r2(total - deposit) };
}

/** 기준 월(오픈 월) — 정한 것, 없으면 가장 이른 투자 월 */
export function startMonthOf(inv) {
  if (inv && isMonth(inv.openMonth)) return inv.openMonth;
  const ms = ((inv && inv.items) || []).map(it => it.start).filter(isMonth).sort();
  return ms[0] || '';
}

/**
 * 누적 손익 · 투자 회수
 * @param monthly [{ month, revenue, costs(감가상각비 포함), dep }] — 기준 월부터 이번 달까지
 * @returns { cumRevenue, cumProfit, cumMargin(%), cumCash(순이익+감가상각비), target, recovered, rate(%),
 *            avgCash(최근 3개월 평균), monthsLeft, expected('YYYY-MM'), recoveredAt, months }
 */
export function paybackSummary(items = [], monthly = [], current = '') {
  const rows = (monthly || []).filter(r => r && isMonth(r.month)).sort((a, b) => a.month.localeCompare(b.month));
  let cumRevenue = 0; let cumProfit = 0; let cumCash = 0; let recoveredAt = null;
  const { recoverable } = investTotals(items);
  for (const r of rows) {
    const rev = Number(r.revenue) || 0; const cost = Number(r.costs) || 0; const dep = Number(r.dep) || 0;
    cumRevenue += rev; cumProfit += rev - cost; cumCash += rev - cost + dep;
    if (!recoveredAt && recoverable > 0 && cumCash >= recoverable) recoveredAt = r.month;
  }
  const withData = rows.filter(r => (Number(r.revenue) || 0) > 0).slice(-3);
  const avgCash = withData.length ? withData.reduce((a, r) => a + (Number(r.revenue) || 0) - (Number(r.costs) || 0) + (Number(r.dep) || 0), 0) / withData.length : 0;
  const remaining = Math.max(0, recoverable - cumCash);
  const monthsLeft = recoverable > 0 && remaining > 0 && avgCash > 0 ? Math.ceil(remaining / avgCash) : (remaining === 0 && recoverable > 0 ? 0 : null);
  const cur = isMonth(current) ? current : (rows.length ? rows[rows.length - 1].month : '');
  return {
    months: rows.length,
    cumRevenue: r2(cumRevenue), cumProfit: r2(cumProfit), cumCash: r2(cumCash),
    cumMargin: cumRevenue > 0 ? Math.round((cumProfit / cumRevenue) * 1000) / 10 : null,
    target: recoverable, recovered: r2(Math.max(0, Math.min(recoverable, cumCash))),
    rate: recoverable > 0 ? Math.round((Math.max(0, cumCash) / recoverable) * 1000) / 10 : null,
    remaining: r2(remaining), avgCash: r2(avgCash), monthsLeft,
    expected: monthsLeft != null && monthsLeft > 0 && cur ? addMonths(cur, monthsLeft) : null,
    recoveredAt,
  };
}
